import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaSync } from 'react-icons/fa';
import analyticsApiService from '../services/analyticsApi';

const formatNumber = (num) => {
  if (num === null || num === undefined) return '0';
  return new Intl.NumberFormat('ru-RU').format(Math.round(num));
};

const formatCurrency = (num) => {
  if (num === null || num === undefined) return '₽0';
  return `₽${formatNumber(num)}`;
};

const CategoryStats = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedEstablishment, setSelectedEstablishment] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('week');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [timeFrom, setTimeFrom] = useState('');
  const [timeTo, setTimeTo] = useState('');
  const [establishments, setEstablishments] = useState([]);

  const [rows, setRows] = useState([]);

  const totalRevenue = useMemo(() => {
    return (rows || []).reduce((sum, r) => sum + (Number(r.total_revenue) || 0), 0);
  }, [rows]);

  const loadEstablishments = useCallback(async () => {
    try {
      const data = await analyticsApiService.getEstablishments();
      setEstablishments(Array.isArray(data) ? data : []);
    } catch (e) {
      // не критично
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        establishment: selectedEstablishment !== 'all' ? selectedEstablishment : undefined,
        period: selectedPeriod !== 'custom' ? selectedPeriod : undefined,
        start_date: selectedPeriod === 'custom' ? (dateFrom || undefined) : undefined,
        end_date: selectedPeriod === 'custom' ? (dateTo || undefined) : undefined,
        time_from: timeFrom || undefined,
        time_to: timeTo || undefined,
      };
      const data = await analyticsApiService.getCategoryStats(params);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError('Не удалось загрузить статистику по категориям');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [selectedEstablishment, selectedPeriod, dateFrom, dateTo, timeFrom, timeTo]);

  useEffect(() => {
    loadEstablishments();
  }, [loadEstablishments]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Отчёты • Категории</h1>
          <p className="text-gray-600">Подробная статистика продаж по категориям</p>
        </div>

        <div className="flex flex-col sm:flex-row flex-wrap gap-3">
          <select
            value={selectedEstablishment}
            onChange={(e) => setSelectedEstablishment(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Все заведения</option>
            {establishments.map((est) => (
              <option key={est.id} value={est.id}>{est.name}</option>
            ))}
          </select>

          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="today">Сегодня</option>
            <option value="week">Неделя</option>
            <option value="month">Месяц</option>
            <option value="quarter">Квартал</option>
            <option value="year">Год</option>
            <option value="custom">Произвольно</option>
          </select>

          {selectedPeriod === 'custom' && (
            <>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                title="С какого числа"
              />
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                title="По какое число"
              />
            </>
          )}

          {/* Фильтр по времени суток */}
          <div className="flex items-center gap-1">
            <span className="text-sm text-gray-500 whitespace-nowrap">Время:</span>
            <input
              type="time"
              value={timeFrom}
              onChange={(e) => setTimeFrom(e.target.value)}
              className="px-2 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              title="Начало периода (время)"
            />
            <span className="text-gray-400">—</span>
            <input
              type="time"
              value={timeTo}
              onChange={(e) => setTimeTo(e.target.value)}
              className="px-2 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              title="Конец периода (время)"
            />
            {(timeFrom || timeTo) && (
              <button
                onClick={() => { setTimeFrom(''); setTimeTo(''); }}
                className="text-gray-400 hover:text-gray-600 text-lg leading-none px-1"
                title="Сбросить время"
              >×</button>
            )}
          </div>

          <button
            onClick={loadData}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <FaSync className="w-4 h-4" />
            Обновить
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Категорий: <strong>{rows.length}</strong>
            </div>
            <div className="text-sm text-gray-600">
              Выручка (всего): <strong>{formatCurrency(totalRevenue)}</strong>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">#</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Категория</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Позиции</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Продано, шт</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Заказов</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Выручка</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Доля</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {rows.map((r, idx) => {
                  const share = totalRevenue > 0 ? ((Number(r.total_revenue) || 0) / totalRevenue) * 100 : 0;
                  return (
                    <tr key={r.category_id || `${r.category_name}-${idx}`} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-500">{idx + 1}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{r.category_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right">{formatNumber(r.dishes_count)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right">{formatNumber(r.quantity_sold)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right">{formatNumber(r.orders_count)}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">{formatCurrency(r.total_revenue)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 text-right">{share.toFixed(1)}%</td>
                    </tr>
                  );
                })}

                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">
                      Нет данных за выбранный период
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryStats;


